<div class="content-wrapper" [class.edit-mode]="editMode" [class.repositioning-context]="repositioning">
    <!-- Dimmed "ghost" copy of the SAME image, unclipped so it spills to its full crop size — shows
         the part that's about to be cut off, greyed out, the way Canva's own crop tool does. Sits
         BEFORE the real image in DOM order so the crisp copy paints on top of it within the frame (see
         .ghost-context in the stylesheet for why no extra z-index is needed for that) — but the same
         drag/suppress bindings as the real image below are wired here too, so a drag can also START
         from the grey spillover area, not just the small crisp frame (clip-path means the crisp image
         only receives pointer events within its own visible bounds, so these never double-fire for the
         same point — whichever of the two is actually visible there is what gets the event). No
         dblclick/wheel/tooltip bindings — this element only ever exists WHILE already repositioning, so
         entering/zooming stays owned entirely by the real image. -->
    <pv-image-ref
        *ngIf="repositioning && !isUnset"
        class="content ghost-context no-print"
        [class.full-image]="configFormGroup.value.fillArea"
        [fileRef]="imageControl.value?.fileRef"
        [transformId]="c_fit"
        (pointerdown)="onPointerDown($event)"
        (mousedown)="suppressGridsterDrag($event)"
        (touchstart)="suppressGridsterDrag($event)"
        >
    </pv-image-ref>

    <!-- Show pv-image-ref only once an image is set; otherwise the placeholder fallback. -->
    <pv-image-ref
        *ngIf="!isUnset; else unsetImage"
        class="content"
        [style.--target-radius]="configFormGroup.value.radius ? configFormGroup.value.radius+'%' : '0%'"
        [class.full-image]="configFormGroup.value.fillArea"
        [class.repositioning]="repositioning"
        [class.reposition-hint]="!repositioning && editMode && configFormGroup.value.fillArea"
        [fileRef]="imageControl.value?.fileRef"
        [transformId]="c_fit"
        matTooltip="Double-click to reposition"
        [matTooltipDisabled]="repositioning || !editMode || !configFormGroup.value.fillArea"
        matTooltipShowDelay="400"
        (naturalSize)="onNaturalSize($event)"
        (pointerdown)="onPointerDown($event)"
        (mousedown)="suppressGridsterDrag($event)"
        (touchstart)="suppressGridsterDrag($event)"
        (wheel)="onWheel($event)"
        (dblclick)="onImageDoubleClick($event)"
        >
    </pv-image-ref>

    <!-- A dedicated sibling rather than styling pv-image-ref directly: that element's own <img> child
         is position:absolute and, once zoomed, larger than the frame — an absolutely-positioned child
         always paints OVER its parent's own box-shadow/border wherever they overlap, which silently
         hid a boundary marker placed directly on the frame once the image spilled past its edge. -->
    <div *ngIf="repositioning" class="reposition-boundary no-print"></div>

    <span *ngIf="repositioning" class="zoom-badge">{{ configFormGroup.value.zoom || 1 | number:'1.0-1' }}x</span>

    <button *ngIf="repositioning" mat-icon-button class="exit-reposition-btn no-print" matTooltip="Done Repositioning" (click)="exitReposition($event)">
        <mat-icon class="material-symbols-outlined">close</mat-icon>
    </button>

    <ng-template #unsetImage>
        <div *ngIf="editMode" class="component-placeholder">
            <mat-icon class="material-symbols-outlined placeholder-icon">image</mat-icon>
            <span class="placeholder-title">Image</span>
            <span class="placeholder-hint">Open the menu to select an image</span>
        </div>
    </ng-template>

    <div *ngIf="editMode" class="content-options no-print">
        <button
            mat-icon-button
            matTooltip="Item Actions"
            [matMenuTriggerFor]="optionMenu"
        >
            <mat-icon>more_vert</mat-icon>
        </button>
    </div>
</div>

<mat-menu #optionMenu>
    <ng-template matMenuContent>
        <button mat-menu-item (click)="edit()">
            <mat-icon>edit</mat-icon>
            <span>Edit Item </span>
        </button>

        <button mat-menu-item (click)="remove()">
            <mat-icon>delete</mat-icon>
            <span>Delete Item</span>
        </button>

        <!-- Duplicate temporarily hidden
        <button mat-menu-item (click)="makeDuplicate()">
            <mat-icon>content_copy</mat-icon>
            <span>Duplicate</span>
        </button>
        -->

        <button mat-menu-item [matMenuTriggerFor]="imageOptions">
            <mat-icon class="material-symbols-outlined">
                image
            </mat-icon>
            <span>Image Options</span>
        </button>

        <button mat-menu-item [matMenuTriggerFor]="layering">
            <mat-icon class="material-symbols-outlined">
                stack
            </mat-icon>
            <span>Layering ({{ layerIndex }})</span>
        </button>
    </ng-template>
</mat-menu>

<mat-menu #imageOptions="matMenu">
    <button mat-menu-item (click)="roundImage()">
        <mat-icon class="material-symbols-outlined">
            {{ configFormGroup.value.radius == 0 ? 'circle' : 'square' }}
        </mat-icon>
        <span>{{ configFormGroup.value.radius == 0 ? 'Round Image' : 'Square Image' }}</span>
    </button>

    <button mat-menu-item (click)="fillArea()">
        <mat-icon class="material-symbols-outlined">
            {{ configFormGroup.value.fillArea ? 'fullscreen_exit' : 'fullscreen' }}
        </mat-icon>
        <span>{{ configFormGroup.value.fillArea ? 'Standard Size' : 'Fill Area' }}</span>
    </button>

    <button mat-menu-item (click)="resetPosition()" [disabled]="!configFormGroup.value.fillArea">
        <mat-icon class="material-symbols-outlined">restart_alt</mat-icon>
        <span>Reset Position</span>
    </button>
</mat-menu>

<mat-menu #layering="matMenu">
    <button mat-menu-item (click)="moveToTop()">
        <mat-icon class="material-symbols-outlined">
            arrow_shape_up_stack
        </mat-icon>
        <span>Send to Top</span>
    </button>
    <button mat-menu-item (click)="moveForward()">
        <mat-icon class="material-symbols-outlined">
            arrow_shape_up
        </mat-icon>
        <span>Move Forward</span>
    </button>
    <button mat-menu-item (click)="moveDown()">
        <mat-icon class="material-symbols-outlined flipped-icon">
            arrow_shape_up
        </mat-icon>
        <span>Move Down</span>
    </button>
    <button mat-menu-item (click)="moveToBottom()">
        <mat-icon class="material-symbols-outlined flipped-icon">
            arrow_shape_up_stack
        </mat-icon>
        <span>Send to Back</span>
    </button>
</mat-menu>